import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { formatDate } from '../format/DateTime';
import BaseHtmlTable from '../table/BaseHtmlTable';

type NoteRow = {
  date: unknown;
  user: string;
  note: string;
};

type NoteDraft = {
  note: string;
};

type NotesProps = {
  headingText?: string;
  showAdd?: boolean;
  showList?: boolean;
  adding?: boolean;
  newNote?: NoteDraft;
  objectNameText?: string;
  notes: NoteRow[];
  rows?: number;
  embedded?: boolean;
  onAdd?(draft: NoteDraft): void | Promise<void>;
};

const Notes: React.FC<NotesProps> = (props) => {
  const objectNameText = props.objectNameText ?? 'Note';
  const buttonText = `Add ${objectNameText}s`;
  const showAdd = props.showAdd !== false;
  const showList = props.showList !== false;

  const [localNote, setLocalNote] = useState('');

  const noteValue = props.newNote ? props.newNote.note : localNote;
  const setNoteValue = (next: string) => {
    if (props.newNote) {
      props.newNote.note = next;
      return;
    }
    setLocalNote(next);
  };

  const handleAdd = async () => {
    if (!props.onAdd || !noteValue.trim()) {
      return;
    }
    await props.onAdd({ note: noteValue });
    if (!props.newNote) {
      setLocalNote('');
    }
  };

  const columnHelper = createColumnHelper<NoteRow>();

  const columnDefs = useMemo(
    () => [
      columnHelper.accessor('date', {
        id: 'date',
        header: 'Date',
        cell: (ctx) => formatDate(ctx.getValue(), 'MM/DD/YYYY HH:mm:ss'),
      }),
      columnHelper.accessor('user', {
        id: 'user',
        header: 'User',
      }),
      columnHelper.display({
        id: 'note',
        header: `${objectNameText}s`,
        cell: (ctx) => {
          const row = ctx.row.original;
          if (row.user === 'System') {
            return <span dangerouslySetInnerHTML={{ __html: row.note }} />;
          }
          return row.note;
        },
      }),
    ],
    [columnHelper, objectNameText]
  );

  const body = (
    <>
      {showAdd && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleAdd();
          }}>
          <div className="row d-print-none">
            <div className="col-md-4 form-group">
              <Form.Control
                as="textarea"
                rows={props.rows ?? 3}
                value={noteValue}
                placeholder={`${objectNameText}s`}
                onChange={(e) => setNoteValue(e.target.value)}
                disabled={props.adding === true}
              />
            </div>
            <div className="form-group">
              <Button
                type="submit"
                disabled={props.adding === true || !noteValue.trim()}>
                {props.adding === true ? 'Adding...' : buttonText}
              </Button>
            </div>
          </div>
        </Form>
      )}

      {showList && (
        <div className="row mt-2">
          <div className="col-md-12">
            <BaseHtmlTable columnDefs={columnDefs} rows={props.notes} showPagination={false} />
          </div>
        </div>
      )}
    </>
  );

  if (props.embedded) {
    return body;
  }

  return (
    <Card className="card-default">
      <Card.Header>{props.headingText || 'Internal Notes'}</Card.Header>
      <Card.Body>{body}</Card.Body>
    </Card>
  );
};

export default Notes;
